import React, { useEffect, useRef, useState } from 'react';
import { normalizeRotation, rotateBy, rotationLabel } from '../../videoUpload/rotation';

// The camera-mount correction for a project that has not been combined yet,
// riding the clip list's chips row directly above the posters it turns.
//
// Placement is the design: the user discovers this problem by LOOKING at their
// footage, so the fix belongs in the same eyeful as the symptom. In a settings
// drawer it would be a thing you have to already know exists. That is also why
// it appears before the combine — upside-down thumbnails are the first sight of
// the problem, and a control that only showed up afterwards would leave the
// user staring at it with nothing to click.
//
// ONE form at every angle. The control grows in place when there is an angle to
// report (state readout, Reset, and the "your originals aren't changed" line)
// and shrinks back when there isn't, but it never moves and never unmounts:
// clicking ↻ used to swap this for a band above the list, teleporting the
// button out from under the cursor on the very first turn.
//
// Two nudge buttons rather than a 0/90/180/270 picker, because the task is
// "turn it until it looks right", not "know that your camera was 180° out".
// The preview turns under the cursor, so the control is checked by eye —
// which is exactly how someone judges whether a building is the right way up.
//
// No Save button: each click persists, the same as the reorder arrows on the
// clip list above. There is nothing to batch and nothing to lose — the write
// is one integer and the change costs nothing to undo.
const FootageRotationControl = ({
  rotation,
  // (nextRotation) => Promise. Rejects with a user-facing Error message.
  onChange,
  // A combine or upload is in flight. Rotation itself is harmless mid-combine
  // (it changes no stored bytes), but the control is locked with the rest of
  // the footage surface so the tab has one consistent "busy" state rather than
  // one live control among frozen ones.
  disabled = false,
  // The failed-save message, owned by the CALLER rather than held here. The tab
  // holds it so it outlives the clip LIST — which unmounts whenever the footage
  // empties and refills — and so the pre-combine and player-bar paths report a
  // failure the same way. This renders it and nothing more.
  error = null,
}) => {
  const [pending, setPending] = useState(false);
  const current = normalizeRotation(rotation);
  const locked = disabled || pending || !onChange;

  const rightRef = useRef(null);

  // Reset removes ITSELF on success — landing back at 0 hides the state line
  // and the button with it — so the element the user just activated is gone and
  // keyboard focus would fall to <body>. It is handed to a turn button, which
  // is what they would reach for next anyway. This handoff belongs to RESET
  // ALONE, not to "landing at 0": a turn button (e.g. ↺ from 90) can also land
  // at 0, but that button never unmounts, so stealing its focus for no reason
  // would be its own bug. Only a SUCCESSFUL reset arms this: a rejected write
  // rolls the angle back, Reset stays, and focus belongs on the button the
  // user is about to press again.
  //
  // Done in an effect rather than inline in `apply` because the turn buttons
  // are still disabled while the write is in flight, and focus() on a disabled
  // button is a no-op. By the time this runs `pending` has cleared and they are
  // focusable again.
  const handOffFocus = useRef(false);
  useEffect(() => {
    if (pending || !handOffFocus.current) return;
    // Spent as soon as the write settles, so a later re-render cannot pull
    // focus out of wherever the user has since moved it.
    handOffFocus.current = false;
    // A no-op if the surface has locked in the meantime, which is the right
    // outcome: focus stays put rather than landing on a dead button.
    rightRef.current?.focus();
  });

  const apply = async (next, { handoffOnSuccess = false } = {}) => {
    if (locked || next === current) return;
    setPending(true);
    try {
      await onChange(next);
      if (handoffOnSuccess) handOffFocus.current = true;
    } catch {
      // The message is the caller's to show (see `error` above). Caught only so
      // a rejected write cannot escape this click handler as an unhandled
      // rejection, and so `finally` re-enables the buttons for another try.
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <span className="pv-rotation-compact" data-testid="footage-rotation-compact">
        <span className="stat-label">Rotate</span>
        <span className="pv-rotation-buttons">
          <button
            type="button"
            className="pi-video-move"
            aria-label="Rotate footage 90 degrees left"
            title="Rotate left"
            disabled={locked}
            onClick={() => apply(rotateBy(current, -1))}
          >
            ↺
          </button>
          <button
            type="button"
            ref={rightRef}
            className="pi-video-move"
            aria-label="Rotate footage 90 degrees right"
            title="Rotate right"
            disabled={locked}
            onClick={() => apply(rotateBy(current, 1))}
          >
            ↻
          </button>
        </span>
        {/* The readout and the way out, AFTER the buttons so that growing the
            control does not shove ↺/↻ sideways under the cursor mid-gesture.
            Both are withheld at rotation 0: "None" and a Reset for nothing read
            as a state the user has failed to reach. */}
        {current !== 0 && (
          <>
            <span className="pv-rotation-state" data-testid="footage-rotation-state">
              {rotationLabel(current)}
            </span>
            <button
              type="button"
              className="pv-rotation-reset"
              data-testid="footage-rotation-reset"
              disabled={locked}
              onClick={() => apply(0, { handoffOnSuccess: true })}
            >
              Reset
            </button>
          </>
        )}
        {/* A span, not a <p>: this sits inside a flex row of chips, and a block
            element there would break the row. A failed save still has to be
            seen — the alternative is discovering it at render, on a video the
            user has already paid credits for. */}
        {error && (
          <span className="pv-rotation-error" data-testid="footage-rotation-error">{error}</span>
        )}
      </span>
      {/* The one line that stops this looking expensive: a user who has just
          waited out a 200GB upload has no way to know that straightening the
          picture is not another one. A SIBLING of the control rather than a
          child, so that it is a direct item of the chips flex row the parent
          drops us into and can take flex-basis:100% to wrap onto its own line
          underneath. Inside the control it would stretch the chips row instead. */}
      {current !== 0 && (
        <span className="pv-rotation-note pv-rotation-note--inline">
          Applied when your video is rendered. Your original files aren&apos;t changed.
        </span>
      )}
    </>
  );
};

export default FootageRotationControl;
